export const ACCOUNT_SETTINGS_KEY = "ns.dispatch.account";

export const MIN_ACCOUNT_PASSWORD = 8;
export const MAX_ACCOUNT_PASSWORD = 128;

export type NanodeAccountSession = {
  email: string;
  accountId: string;
  token: string;
  baseUrl: string;
};

export function normalizeAccountEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

export function validateAccountEmail(raw: string): { ok: true; email: string } | { ok: false; error: string } {
  const email = normalizeAccountEmail(raw);
  if (!email) return { ok: false, error: "Enter an email address." };
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { ok: false, error: "Email does not look valid." };
  if (email.length > 120) return { ok: false, error: "Email is too long." };
  return { ok: true, email };
}

export function validateAccountPassword(raw: string): { ok: true; password: string } | { ok: false; error: string } {
  if (!raw) return { ok: false, error: "Enter a password." };
  if (/\s/.test(raw)) return { ok: false, error: "Password cannot contain spaces." };
  if (raw.length < MIN_ACCOUNT_PASSWORD) {
    return { ok: false, error: `Password must be at least ${MIN_ACCOUNT_PASSWORD} characters.` };
  }
  if (raw.length > MAX_ACCOUNT_PASSWORD) {
    return { ok: false, error: `Password must be at most ${MAX_ACCOUNT_PASSWORD} characters.` };
  }
  return { ok: true, password: raw };
}

export function loadAccountSession(storage: Pick<Storage, "getItem"> | null | undefined): NanodeAccountSession | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(ACCOUNT_SETTINGS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NanodeAccountSession>;
    if (!parsed.email || !parsed.accountId || !parsed.token || !parsed.baseUrl) return null;
    return {
      email: String(parsed.email),
      accountId: String(parsed.accountId),
      token: String(parsed.token),
      baseUrl: String(parsed.baseUrl),
    };
  } catch {
    return null;
  }
}

export function saveAccountSession(
  session: NanodeAccountSession,
  storage: Pick<Storage, "setItem"> | null | undefined,
): void {
  if (!storage) return;
  storage.setItem(ACCOUNT_SETTINGS_KEY, JSON.stringify(session));
}

export function clearAccountSession(storage: Pick<Storage, "removeItem"> | null | undefined): void {
  storage?.removeItem(ACCOUNT_SETTINGS_KEY);
}
